import {
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
} from '@angular/core'
import { FormArray, FormBuilder, FormGroup } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { isContentEditableEnabled } from '@containers/i18n/store/i18n.selector'
import { Store, select } from '@ngrx/store'
import { isListDataExportLoading } from '@shared/shared.select'
import { TableFilterMatching, TableFilterType } from '@shared/shared.types'
import { filtersToDbQuery } from '@shared/shared.utils'
import { Subscription } from 'rxjs'

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, OnChanges {
  @ContentChildren(TemplateRef) templates: QueryList<TemplateRef<any>>
  @Input() public rows: any = []
  @Input() public noDataTemplate: TemplateRef<any>
  @Input() public filters: any = []
  @Input() public sort: string
  @Input() public orderBy: string
  @Input() public hideFooter: boolean = false
  @Input() public showDownload: boolean = false
  @Input() public showFilter: boolean = false
  @Input() public showReload: boolean = false
  @Input() public showSearch: boolean = false
  @Input() public allowMultipleFilters: boolean = true
  @Input() public searchKeyword: string = ''
  @Input() public totalItems: number = 0
  @Input() public totalResults?: number
  @Input() public itemsPerPage: any = 20
  @Input() public currentPage: any = 1
  @Input() public columns: any = []
  public itemsPerPageOptions = [
    { label: '10', value: 10 },
    { label: '15', value: 15 },
    { label: '20', value: 20 },
    { label: '50', value: 50 },
  ]
  private debounceTimeout: any
  private searchTimeout: any
  @Input() public isLoading: boolean = false
  @Output() download: EventEmitter<any> = new EventEmitter()
  @Output() refresh: EventEmitter<any> = new EventEmitter()
  @Output() onPageChange: EventEmitter<any> = new EventEmitter()
  @Output() onFilterChange: EventEmitter<any> = new EventEmitter()
  @Output() onPageSizeChange: EventEmitter<any> = new EventEmitter()
  @Output() onSort: EventEmitter<any> = new EventEmitter()
  @Output() onSearchChange: EventEmitter<string> = new EventEmitter()
  public filtersPrev: any = []
  public _currentPage = 1
  public pages: number
  public totalResultsPassed = false
  public showFilters = false
  public _filters: FormGroup<any>
  public filterFields = []
  public fieldSubscriptions$: { [key: string]: Subscription } = {}
  public filterFieldsKeys = []
  public filterTimeout: any
  public filter$: Subscription
  public filterMatchingByType = {
    [TableFilterType.Number]: [
      TableFilterMatching.Equals,
      TableFilterMatching.NotEquals,
      TableFilterMatching.GreaterThanOrEqualTo,
      TableFilterMatching.LessThanOrEqualTo,
      // TableFilterMatching.GreaterThan,
      // TableFilterMatching.LessThan,
      TableFilterMatching.IsEmpty,
      TableFilterMatching.IsNotEmpty,
    ],
    [TableFilterType.String]: [
      TableFilterMatching.Includes,
      TableFilterMatching.Is,
      TableFilterMatching.IsNot,
      TableFilterMatching.IsEmpty,
      TableFilterMatching.IsNotEmpty,
    ],
    [TableFilterType.Date]: [],
    [TableFilterType.Boolean]: [],
  }
  public isExporting: boolean = false
  public isExporting$: Subscription
  public select$: { [key: string]: Subscription } = {}
  public select: { [key: string]: any[] } = {}
  options: any = {
    path: '/assets/loader.json',
  }
  public isTranslationActive: boolean = false
  public isTranslationActive$: Subscription

  constructor(
    private formBuilder: FormBuilder,
    public router: Router,
    private route: ActivatedRoute,
    private store: Store<any>,
  ) {}

  getFieldType(field: string) {
    const targetField = this.filterFields.find(
      ({ prop, filterKey }) => prop === field || filterKey === field,
    )

    return targetField?.filterType
  }

  getFieldKeys(field: string) {
    const targetField = this.filterFields.find(
      ({ prop, filterKey }) => prop === field || filterKey === field,
    )

    return targetField?.fieldKeys
  }

  matchingIsEmpty(matching: string) {
    return (
      matching === TableFilterMatching.IsEmpty ||
      matching === TableFilterMatching.IsNotEmpty
    )
  }

  parseInt(val: string) {
    return val ? parseInt(val) : undefined
  }

  isMatchingFixed(field: string) {
    return this.select$[field] ? true : false
  }

  isDate(type: any) {
    return type === TableFilterType.Date
  }

  isBoolean(type: any) {
    return type === TableFilterType.Boolean
  }

  filtersApplied() {
    const isValid = this.isFormValid()
    if (isValid) {
      return this.filterRows.length
    }
    return 0
  }

  initFilters() {
    if (this.filter$) {
      this.filter$.unsubscribe()
    }

    this.filterFields = this.columns
      .filter(({ filterable }) => filterable)
      .map(({ filterType, prop, name, filterKey, select$, filterFields }) => {
        if (select$) {
          this.select$[prop] = select$.subscribe((data: any) => {
            this.select[prop] = data.toJS()
          })
        }

        return { filterType, prop, name, filterKey, filterFields }
      })
    this._filters = this.formBuilder.group({
      rows: this.formBuilder.array([this.createFilter()]),
    })
    this.filterFieldsKeys = this.filterFields.map(({ name }) => name)
    this.filter$ = this._filters.valueChanges.subscribe(
      this.onFilter.bind(this),
    )
    this.subscribeRowFieldChange()
  }

  createFilter(): FormGroup {
    const firstRow = this.filterFields[0] || {}

    return this.formBuilder.group({
      field: [firstRow.filterKey || firstRow.prop],
      fieldKeys: [firstRow.filterFields],
      operator: ['or'],
      matching: [this.select$[firstRow.prop] || firstRow.filterType === TableFilterType.Boolean ? TableFilterMatching.Is : ''],
      value: [''],
      range_from: [''],
      range_to: [''],
      type: [firstRow.filterType],
    })
  }

  isFormValid(cb?: any) {
    const rows = this.filterRows.value
    let value: boolean

    if (!rows.length) {
      value = true
    } else {
      value = rows.reduce((prev: any, current: any) => {
        if (
          current.matching === TableFilterMatching.IsNotEmpty ||
          current.matching === TableFilterMatching.IsEmpty
        ) {
          return prev
        } else if (prev && current.type === TableFilterType.Date) {
          return current.range_from || current.range_to
        } else if (prev && current.type === TableFilterType.Boolean) {
          return current.value !== null && current.value !== ''
        } else {
          return (
            prev &&
            typeof current.value !== undefined &&
            current.value !== null &&
            current.value !== ''
          )
        }
      }, true)
    }

    if (value && cb) {
      cb()
    }

    return value
  }

  onFilter() {
    clearTimeout(this.filterTimeout)

    this.filterTimeout = setTimeout(() => {
      this.isFormValid(() =>
        this.onFilterChange.emit({ filters: this.filterRows.value }),
      )
    }, 800)
  }

  getSelect(field: string) {
    if (this.select[field]) {
      return this.select[field]
    }

    return []
  }

  getSelectTranslationKeys(field: string) {
    if (this.select[field]) {
      return this.select[field]?.map((item: any) => {
        return item.translationKey
      })
    }
  }

  getSelectTranslationDefaults(field: string) {
    if (this.select[field]) {
      return this.select[field]?.reduce((acc: any, item: any) => {
        acc[item.translationKey] = item.title
        return acc
      }, {})
    }
  }

  handleFieldChange(row: number) {
    return (field: string) => {
      const fieldType = this.getFieldType(field)
      let firstMatching = this.filterMatchingByType[fieldType][0]

      if (this.select$[field] || fieldType === TableFilterType.Boolean) {
        firstMatching = TableFilterMatching.Is
      }

      this.filterRows.controls[row].get('matching').setValue(firstMatching)
      this.filterRows.controls[row].get('type').setValue(fieldType)
      this.filterRows.controls[row].get('value').setValue(null)
      this.filterRows.controls[row]
        .get('fieldKeys')
        .setValue(this.getFieldKeys(field))
    }
  }

  addFilter(): void {
    this.filterRows.push(this.createFilter())
    this.subscribeRowFieldChange()
  }

  subscribeRowFieldChange() {
    const index = this.filterRows.controls.length - 1
    const row = this.filterRows.controls[index] as any

    this.fieldSubscriptions$[index] = row
      .get('field')
      .valueChanges.subscribe(this.handleFieldChange(index))
  }

  unsubscribeRowFieldChange(row: number) {
    this.fieldSubscriptions$[row].unsubscribe()
    this.fieldSubscriptions$[row] = undefined
  }

  removeFilter(index: number): void {
    this.filterRows.removeAt(index)
    this.unsubscribeRowFieldChange(index)

    // Close the dropdown when removing a filter
    this.showFilters = false
  }

  ngOnInit() {
    this.initFilters()

    this._currentPage = this.currentPage

    this.isExporting$ = this.store
      .pipe(select(isListDataExportLoading))
      .subscribe((state) => {
        this.isExporting = state
      })

    this.isTranslationActive$ = this.store
      .pipe(select(isContentEditableEnabled))
      .subscribe((isTranslationActive) => {
        this.isTranslationActive = isTranslationActive
      })
  }

  get filterRows() {
    return this._filters.get('rows') as FormArray
  }

  onSubmit(): void {
    console.log(this._filters.value)
  }
  ngOnChanges(changes: SimpleChanges) {
    if (JSON.stringify(this.filters) !== JSON.stringify(this.filtersPrev)) {
      if (!this.filters.length) {
        this.initFilters()
      }

      this.filtersPrev = this.filters
    }

    if (this.totalResults !== undefined) {
      this.totalResultsPassed = true
    }
    if (this.itemsPerPage) {
      const length = Math.ceil(
        (this.totalResults || this.totalItems) / this.itemsPerPage,
      )

      this.pages = Math.ceil(length)
    }

    if (changes['columns']) {
      this.initFilters()
    }
  }

  getTemplateRef(name: string) {
    if (this.templates) {
      const templates = this.templates.toArray()
      const template = templates.filter((ref: any) => {
        const keys = ref._declarationTContainer.localNames
        if (keys) {
          return keys[0] === name
        }
        return false
      })

      if (template.length) {
        return template[0]
      }
    }
    return null
  }

  handlePageChange(e: any): void {
    let value = e.target ? e.target.value : e

    if (!value) {
      value = 1
    }

    this._currentPage = parseInt(value) || 1

    clearTimeout(this.debounceTimeout)

    this.debounceTimeout = setTimeout(
      () => {
        this.onPageChange.emit(parseInt(value) || 1)
      },
      e.target ? 500 : 0,
    )

    if (this.showFilter) {
      clearTimeout(this.filterTimeout)

      this.filterTimeout = setTimeout(
        () => {
          this.onFilterChange.emit({
            pagination: {
              page: this._currentPage,
              page_size: this.itemsPerPage,
              order_by: this.orderBy,
              sort: this.sort,
            },
          })
        },
        !!e.target ? 800 : 0,
      )
    }
  }

  get pageInputWidth() {
    return 13 + this._currentPage.toString().length * 8 + 'px'
  }

  handlePageSizeChange(pageSize: any): void {
    this.onPageSizeChange.emit(parseInt(pageSize, 10))

    if (this.showFilter) {
      this.onFilterChange.emit({
        pagination: {
          page: this.currentPage,
          page_size: parseInt(pageSize, 10),
        },
      })
    }
  }

  handleDownload() {
    this.download.emit()
  }

  handleRefresh() {
    this.initFilters()

    this.onFilterChange.emit({ filters: [] })
  }

  handleSearch(event: any) {
    const keyword = event.target.value

    clearTimeout(this.searchTimeout)

    this.searchTimeout = setTimeout(() => {
      this.onSearchChange.emit(keyword)
    }, 500)
  }

  toggleFilter() {
    this.showFilters = !this.showFilters
  }

  handleClickOutside() {
    this.showFilters = false
  }

  ngOnDestroy() {
    if (this.filter$) {
      this.filter$.unsubscribe()
    }

    Object.keys(this.select$).forEach((sub) => {
      this.select$[sub].unsubscribe()
    })

    this.isExporting$.unsubscribe()
    this.isTranslationActive$.unsubscribe()
  }

  goToPrev(): void {
    const currentPage: number = this._currentPage

    if (currentPage !== 1) {
      this.handlePageChange(currentPage - 1)
    }
  }
  goToNext(): void {
    const currentPage: number = this._currentPage

    if (currentPage !== this.pages) {
      this.handlePageChange(currentPage + 1)
    }
  }

  handleSort(prop: string) {
    this.onSort.emit(prop)

    if (this.showFilter) {
      let order_by: string = prop
      let sort: string

      if (this.orderBy) {
        if (this.sort === 'desc' || this.sort === 'DESC') {
          sort = 'asc'
        } else if (this.sort === 'asc' || this.sort === 'ASC') {
          sort = undefined
          order_by = undefined
        }
      } else {
        sort = 'desc'
      }

      this.onFilterChange.emit({
        pagination: {
          page: this.currentPage,
          page_size: this.itemsPerPage,
          order_by,
          sort,
        },
      })
    }
  }

  handlePageInputClick(e: any) {
    e.target.select()
  }
}
